import React, { useEffect, useState } from "react";
import classnames from "classnames";
import Grid from "@material-ui/core/Grid";
import Radio from "@material-ui/core/Radio";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Typography from "@material-ui/core/Typography";
import { configure, GlobalHotKeys } from "react-hotkeys";
import { YardConverter } from "@prestosports/prestostats-core/Common";
import Toolbar from "@material-ui/core/Toolbar";
import { Box } from "@material-ui/core";
import { SecondaryButton } from "@prestosports/commons-ui";
import fieldZonePositionsStyles from "./styles/FieldZonePositions.styles";
import gameButtonGridStyles from "../../styles/GameButtonGrid.styles";
import YardButton from "./YardButton";
import { useEventContext } from "../../../../state/events/context";
import useFieldConstants from "../utils/FieldConfiguration";
import { useGameContextContext } from "../../../../state/game-context/context";
import { useTeamsContext } from "../../../../state/teams/context";

const PositionKeyListener = ({
	onZoneSelected,
	onPositionSelected,
	fieldLength,
	zones,
	position,
	handleSkip
}) => {
	configure({
		allowCombinationSubmatches: true
	});

	const [currentPosition, setCurrentPosition] = useState("");
	const [currentZone, setCurrentZone] = useState("");

	const keyMap = {
		ZONE: {
			sequences: [..."hvHVabcdABCD"]
		},
		POSITION: {
			sequences: [..."-0123456789"]
		},
		CONFIRM: ["enter"],
		DELETE: ["backspace"],
		CLEAR: ["del"],
		SKIP: ["tab"]
	};

	const positionPattern = /^-?[0-9]?[0-9]$/i;
	const zonePattern = /^[v,h,V,H]$/i;
	const abcdPaterrn = /^[a-dA-D]$/i;

	const isValid = (sequence) => {
		return (
			positionPattern.test(sequence) && parseInt(sequence, 10) <= fieldLength / 2
		);
	};

	const handlers = {
		ZONE: (event) => {
			const zone = event.key.toLowerCase();
			if (currentZone.length === 0 && zonePattern.test(zone)) {
				setCurrentZone(zone);
			} else if (abcdPaterrn.test(zone)) {
				const selectedZone = zones.find(
					(z) => z.displayName?.split(" ")[1] === zone.toUpperCase()
				);
				if (selectedZone) {
					onZoneSelected(selectedZone);
					setCurrentZone(selectedZone.isHome ? "h" : "v");
					setCurrentPosition("");
				}
			}
		},
		POSITION: (event) => {
			const key = event.key.toLowerCase();
			const isNegativePosition =
				currentPosition.includes("-") && currentPosition.length < 3;

			if (
				currentZone.length &&
				(currentPosition.length < 2 || isNegativePosition)
			) {
				const sequence = currentPosition + key;
				if (isValid(sequence) || sequence === "-") {
					setCurrentPosition(sequence);
				}
			}
		},
		CONFIRM: () => {
			if (zonePattern.test(currentZone) && positionPattern.test(currentPosition)) {
				if (onPositionSelected) {
					onPositionSelected(
						currentZone.toUpperCase(),
						parseInt(currentPosition, 10)
					);
				}

				setCurrentZone("");
				setCurrentPosition("");
			} else if (currentZone && !currentPosition) {
				onPositionSelected(currentZone.toUpperCase(), 0);
				setCurrentZone("");
			}
		},
		DELETE: () => {
			if (currentPosition.length === 0) {
				setCurrentZone(currentZone.slice(0, -1));
			}
			setCurrentPosition(currentPosition.slice(0, -1));
		},
		CLEAR: () => {
			setCurrentZone("");
			setCurrentPosition("");
		},
		SKIP: (e) => {
			e.preventDefault();
			if (position && handleSkip) {
				handleSkip(position);
			}
		}
	};

	return (
		<>
			<Typography variant="inherit">{`Please enter the spot of the ball: ${currentZone.toUpperCase()}${currentPosition}...`}</Typography>
			<GlobalHotKeys keyMap={keyMap} handlers={handlers} allowChanges />
		</>
	);
};

const FieldZonePosition = ({
	disableKeyListener,
	position,
	onPositionSelect,
	restrictedPositions,
	showEndZoneYards = false,
	defaultValue,
	fragmentRdValue
}) => {
	const smScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
	const { state: teamState } = useTeamsContext();
	const homeTeam = teamState.homeTeam();
	const awayTeam = teamState.awayTeam();

	const classes = fieldZonePositionsStyles({ homeTeam, awayTeam });
	const gameButtonGridClasses = gameButtonGridStyles();

	const { state } = useEventContext();
	const currentEventSettings = state.currentEventSettings();
	const {
		fieldLength,
		zoneLength,
		zonesPerTeamQty,
		zoneLengthPct,
		endZoneLengthPct,
		endZoneLength
	} = useFieldConstants(currentEventSettings);

	const { state: gameContextState } = useGameContextContext();
	const swapTeams = gameContextState.swapTeams();

	const yardConverter = new YardConverter(fieldLength);

	const [zone, setZone] = useState({});
	const [yard, setYard] = useState();

	const getYardLines = (start, end) => {
		const yards = [];
		if (start !== end) {
			for (let i = Math.min(start, end); i <= Math.max(start, end); i += 1) {
				yards.push(i);
			}
		}

		return yards.sort((a, b) => {
			return a <= fieldLength / 2 ? a - b : b - a;
		});
	};

	const generateZones = (totalLength, zoneYardLength, zoneQuantity) => {
		const zones = [];
		const middle = zoneYardLength * (zoneQuantity / 2);

		const homeStart = 0;
		const homeEnd = showEndZoneYards ? -endZoneLength : homeStart;
		const homeYardLines = getYardLines(homeStart, homeEnd);
		zones.push({
			id: "H",
			displayName: "End Zone",
			range: { start: homeStart, end: homeEnd },
			team: homeTeam,
			isHome: true,
			isEndZone: true,
			width: endZoneLengthPct,
			isRestricted:
				homeStart !== homeEnd &&
				homeYardLines.every((yl) => restrictedPositions.some((rPos) => rPos === yl))
		});
		for (let i = 0; i < zoneQuantity; i += 1) {
			let start = i * zoneYardLength;
			let end = start + zoneYardLength;

			if (start < middle) {
				start += 1;
			}
			if (start >= middle) {
				end -= 1;
			}
			const zonePositions = [...Array(end - start + 1)].map(
				(_, dex) => dex + start
			);

			const displayName = swapTeams
				? `Zone ${String.fromCharCode(68 - zoneQuantity + 1 + i)}`
				: `Zone ${String.fromCharCode(68 - i)}`;
			const isHome = start < middle;
			const teamTag = isHome ? "Home" : "Visitor";

			zones.push({
				id: i,
				displayName: zoneQuantity === 2 ? teamTag : displayName,
				range: { start, end },
				team: isHome ? homeTeam : awayTeam,
				isHome,
				isEndZone: false,
				width: zoneLengthPct,
				isRestricted: !zonePositions.filter(
					(zPos) => restrictedPositions.indexOf(zPos) === -1
				).length
			});
		}

		const visStart = totalLength;
		const visEnd = showEndZoneYards ? totalLength + endZoneLength : visStart;
		const visYardLines = getYardLines(visStart, visEnd);
		zones.push({
			id: "V",
			displayName: "End Zone",
			range: {
				start: visStart,
				end: visEnd
			},
			team: awayTeam,
			isEndZone: true,
			width: endZoneLengthPct,
			isRestricted:
				visStart !== visEnd &&
				visYardLines.every((yl) => restrictedPositions.some((rPos) => rPos === yl))
		});

		// Reversed because Visitor shows on the left.
		if (!swapTeams) {
			zones.reverse();
		}

		return zones;
	};

	const resolveZone = (fieldZones, yardLine) => {
		let resolvedZone = {};
		if (yardLine !== undefined) {
			const yardZone = fieldZones.find(
				(z) =>
					z.range &&
					Math.min(z.range.start, z.range.end) <= yardLine &&
					Math.max(z.range.start, z.range.end) >= yardLine
			);
			resolvedZone = yardZone || {};
		}

		return resolvedZone;
	};

	const onYardSelect = (yardline) => {
		setYard(yardline);
		if (onPositionSelect) {
			onPositionSelect(yardline);
		}
	};

	const onZoneSelect = (selectedZone) => {
		const { isEndZone, range, isRestricted } = selectedZone;
		if (!isRestricted) {
			const { start, end } = range;
			setZone(selectedZone);
			if (isEndZone && start === end) {
				onYardSelect(selectedZone.range.start);
			}
		}
	};

	const isValid = (yardline) => {
		return (
			yardline >= (showEndZoneYards ? -endZoneLength : 0) &&
			yardline <= fieldLength + (showEndZoneYards ? endZoneLength : 0) &&
			!restrictedPositions.some((rPos) => rPos === yardline)
		);
	};

	const areYardsAvailable = (resPos, z) => {
		const { start, end } = z.range;
		for (let i = Math.min(start, end); i <= Math.max(start, end); i += 1) {
			if (!resPos.some((rp) => rp === i)) return true;
		}

		return false;
	};

	const zones = generateZones(fieldLength, zoneLength, zonesPerTeamQty * 2);
	const yardLines = zone.range
		? getYardLines(zone.range.start, zone.range.end)
		: [];

	useEffect(() => {
		setZone(resolveZone(zones, position));
		setYard(position);
	}, [position]);

	const canContinue = () =>
		!fragmentRdValue &&
		(defaultValue || defaultValue === 0) &&
		position === defaultValue;

	return (
		<>
			<Box px={0.5} className={classes.headerBox}>
				<Typography variant="h5" className={classes.sectionTitle}>
					Zone
				</Typography>
				{canContinue() && (
					<SecondaryButton
						variant="contained"
						size="medium"
						onClick={() => {
							onPositionSelect(position);
						}}
						label="Next"
					/>
				)}
			</Box>

			<Grid
				container
				spacing={1}
				align="center"
				className={classnames({ [classes.reducedZoneBarContainer]: smScreen })}
			>
				{zones.map((z) => {
					const identifier = z.isEndZone
						? `zone-${z.displayName}${z.isHome ? "-home" : "-away"}`
						: `zone-${z.displayName}`;
					const yardsAvailable = areYardsAvailable(restrictedPositions, z);
					return (
						<Grid
							item
							onClick={() => yardsAvailable && onZoneSelect(z)}
							key={z.id}
							style={{
								width: `${z.width}%`
							}}
							className={classnames({ [classes.reducedZoneBarItem]: smScreen })}
						>
							<div
								className={classnames(
									classes.zoneBar,
									{ visitor: !z.isHome, [classes.reducedZoneBar]: smScreen },
									!yardsAvailable && classes.disabledZone
								)}
							/>
							<FormControlLabel
								labelPlacement={smScreen ? "bottom" : "end"}
								id={identifier}
								value="z.id"
								control={
									<Radio
										className={classnames({ [classes.disabledZone]: !yardsAvailable })}
										color="primary"
										size={smScreen ? "small" : "medium"}
									/>
								}
								label={z.displayName}
								checked={z.id === zone.id}
								classes={{
									label: classes.zoneBarLabel
								}}
							/>
						</Grid>
					);
				})}
			</Grid>

			{!!yardLines.length && (
				<>
					<Typography variant="h5" className={classes.sectionTitle}>
						Yardline
					</Typography>
					<Grid container>
						{yardLines.map((yardLine) => {
							return (
								<Grid
									item
									key={yardLine}
									className={gameButtonGridClasses.gridItem}
									align="center"
								>
									<YardButton
										yard={yardLine}
										fieldLength={fieldLength}
										selected={yard === yardLine}
										onClick={onYardSelect}
										disabled={restrictedPositions.some((rp) => rp === yardLine)}
									/>
								</Grid>
							);
						})}
					</Grid>
				</>
			)}

			<Toolbar disableGutters>
				{!disableKeyListener && (
					<PositionKeyListener
						fieldLength={fieldLength}
						positions={yardLines}
						onPositionSelected={(zoneValue, positionValue) => {
							const yardline = yardConverter.convertToFieldPosition(
								zoneValue,
								positionValue
							);
							if (isValid(yardline)) {
								onYardSelect(yardline);
							}
						}}
						onZoneSelected={onZoneSelect}
						zones={zones}
						position={position}
						handleSkip={onPositionSelect}
					/>
				)}
			</Toolbar>
		</>
	);
};

export default FieldZonePosition;
